"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";

type ToastVariant = "success" | "error" | "info" | "warning";

/** Inline action rendered as a button on the toast (standard label
 *  "Afturkalla"). Toasts with an action stay ~7s and pause on hover so
 *  the undo window is actually reachable. */
interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastData {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  action?: ToastAction;
  createdAt: number;
}

interface ToastInput {
  title: string;
  description?: string;
  variant: ToastVariant;
  action?: ToastAction;
}

interface ToastContextValue {
  toast: (input: ToastInput) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_DURATION = 4000;
const TOAST_ACTION_DURATION = 7000;
const MAX_TOASTS = 1;

const variantBorderColor: Record<ToastVariant, string> = {
  success: "#059669",
  error: "#DC2626",
  info: "#1446DC",
  warning: "#D97706",
};

let toastCounter = 0;

interface ToastTimer {
  timer: ReturnType<typeof setTimeout> | null;
  /** Epoch ms when the active timer fires; used to compute the remaining
   *  window when the user hovers (pause) and leaves (resume). */
  expiresAt: number;
  remaining: number;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [removing, setRemoving] = useState<Set<string>>(new Set());
  const [walkMode, setWalkMode] = useState(false);
  const timersRef = useRef<Map<string, ToastTimer>>(new Map());

  // The /call + /canvass pages flip ``body[data-walk-mode]`` to hide
  // the sidebar/header and reclaim screen real estate. Reuse that
  // signal to move the toast container out of the bottom-right — on
  // those pages the caller's attention is on the target card and
  // dispatcher cues should land at the top-left, above the voter's
  // name, where they read naturally from top to bottom.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const update = () =>
      setWalkMode(document.body.hasAttribute("data-walk-mode"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-walk-mode"],
    });
    return () => observer.disconnect();
  }, []);

  const removeToast = useCallback((id: string) => {
    setRemoving((prev) => new Set(prev).add(id));
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      setRemoving((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 300);
  }, []);

  const clearTimer = useCallback((id: string) => {
    const entry = timersRef.current.get(id);
    if (entry?.timer) clearTimeout(entry.timer);
    timersRef.current.delete(id);
  }, []);

  const startTimer = useCallback(
    (id: string, duration: number) => {
      const timer = setTimeout(() => {
        timersRef.current.delete(id);
        removeToast(id);
      }, duration);
      timersRef.current.set(id, {
        timer,
        expiresAt: Date.now() + duration,
        remaining: duration,
      });
    },
    [removeToast]
  );

  // Hover pauses auto-dismiss (undo buttons must stay reachable); leaving
  // resumes with whatever window was left.
  const pauseToast = useCallback((id: string) => {
    const entry = timersRef.current.get(id);
    if (!entry?.timer) return;
    clearTimeout(entry.timer);
    entry.timer = null;
    entry.remaining = Math.max(entry.expiresAt - Date.now(), 1000);
  }, []);

  const resumeToast = useCallback(
    (id: string) => {
      const entry = timersRef.current.get(id);
      if (!entry || entry.timer) return;
      startTimer(id, entry.remaining);
    },
    [startTimer]
  );

  const toast = useCallback(
    (input: ToastInput) => {
      const id = `toast-${++toastCounter}`;
      const newToast: ToastData = {
        id,
        title: input.title,
        description: input.description,
        variant: input.variant,
        action: input.action,
        createdAt: Date.now(),
      };

      setToasts((prev) => {
        const next = [...prev, newToast];
        // If over max, remove oldest
        if (next.length > MAX_TOASTS) {
          const removed = next.shift();
          if (removed) {
            clearTimer(removed.id);
            removeToast(removed.id);
          }
        }
        return next;
      });

      startTimer(id, input.action ? TOAST_ACTION_DURATION : TOAST_DURATION);
    },
    [removeToast, clearTimer, startTimer]
  );

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((entry) => {
        if (entry.timer) clearTimeout(entry.timer);
      });
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div
        className={
          walkMode
            ? "fixed left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2.5 pointer-events-none"
            : "fixed bottom-6 right-6 z-[9999] flex flex-col gap-2.5 pointer-events-none"
        }
        style={
          walkMode
            ? {
                // Clear the notch AND the status bar's back button / list pill
                // (both live at the very top of the map). Centered so it never
                // sits on top of either tappable corner.
                top: "calc(env(safe-area-inset-top) + 64px)",
                maxWidth: "92vw",
              }
            : { maxWidth: "380px", minWidth: "280px" }
        }
        aria-live="polite"
        role="status"
      >
        {toasts.map((t) => (
          <ToastItem
            key={t.id}
            toast={t}
            isRemoving={removing.has(t.id)}
            fromLeft={walkMode}
            onPause={() => pauseToast(t.id)}
            onResume={() => resumeToast(t.id)}
            onDismiss={() => {
              clearTimer(t.id);
              removeToast(t.id);
            }}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({
  toast,
  isRemoving,
  fromLeft,
  onPause,
  onResume,
  onDismiss,
}: {
  toast: ToastData;
  isRemoving: boolean;
  fromLeft?: boolean;
  onPause: () => void;
  onResume: () => void;
  onDismiss: () => void;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Trigger slide-in on next frame
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const borderColor = variantBorderColor[toast.variant];
  const isVisible = mounted && !isRemoving;
  // Slide in from whichever edge the container is pinned to.
  const hiddenTransform = fromLeft
    ? "translateX(calc(-100% - 1rem))"
    : "translateX(calc(100% + 1rem))";

  return (
    <div
      className="pointer-events-auto border border-border bg-card text-card-foreground transition-all duration-300 ease-out"
      style={{
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: "0",
        transform: isVisible ? "translateX(0)" : hiddenTransform,
        opacity: isVisible ? 1 : 0,
      }}
      onMouseEnter={onPause}
      onMouseLeave={onResume}
    >
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">{toast.title}</p>
          {toast.description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {toast.description}
            </p>
          )}
          {toast.action && (
            <button
              type="button"
              onClick={() => {
                toast.action?.onClick();
                onDismiss();
              }}
              className="mt-1.5 xd-btn xd-btn-outline text-xs h-7 px-2.5"
            >
              {toast.action.label}
            </button>
          )}
        </div>
        <button
          onClick={onDismiss}
          className="text-muted-foreground hover:text-foreground transition-xd flex-shrink-0 p-0.5"
          aria-label="Loka"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return ctx;
}
